import React, { useState, useEffect } from 'react';
import { X, RefreshCw } from 'lucide-react';
import OfferForm from './OfferForm';
import { getAiSuggestedOffer, regenerateAiSuggestion } from '../services/offerService';
import { toast } from 'sonner';

interface OfferFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  willId: string;
  willTitle: string;
  willMedia?: any[];
  onSuccess?: (offer: any) => void;
  defaultContactInfo?: string;
}

const OfferFormModal: React.FC<OfferFormModalProps> = ({
  isOpen,
  onClose,
  willId,
  willTitle,
  willMedia = [],
  onSuccess,
  defaultContactInfo = '',
}) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [aiSuggestion, setAiSuggestion] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);

  // Handle escape key to close the panel
  useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscKey);
    
    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [isOpen, onClose]);

  // Fetch AI suggestion when modal opens
  useEffect(() => {
    if (isOpen && willId) {
      fetchAiSuggestion();
    }
  }, [isOpen, willId]);

  // Process AI suggestion data to ensure it's properly formatted
  const processAiSuggestion = (data: any) => {
    console.log('Processing AI suggestion data:', data);
    
    // If data is undefined or null, return empty object
    if (!data) {
      return { 
        description: '', 
        price: '', 
        currency: 'ILS', 
        estimatedCompletionDate: '', 
        providerContactInfo: defaultContactInfo 
      };
    }
    
    // If data is a string (possibly JSON), try to parse it
    if (typeof data === 'string') {
      try {
        return processAiSuggestion(JSON.parse(data));
      } catch (e) {
        console.error('Failed to parse AI suggestion data as JSON:', e);
        // If it's just a string and not JSON, use it as the description
        return { 
          description: data, 
          price: '', 
          currency: 'ILS', 
          estimatedCompletionDate: '', 
          providerContactInfo: defaultContactInfo 
        };
      }
    }
    
    // Handle if the real data is nested within a property like 'data', 'suggestion', 'content', etc.
    const possibleContainers = ['data', 'suggestion', 'content', 'result', 'offer', 'response'];
    for (const container of possibleContainers) {
      if (data[container] && typeof data[container] === 'object') {
        return processAiSuggestion(data[container]);
      }
    }
    
    // If data is already an object with the right structure, return it with proper defaults
    if (data && typeof data === 'object') {
      // Process price data - ensure it's in a usable format
      let processedPrice = '';
      
      if (data.price !== undefined && data.price !== null) {
        // Handle numeric prices
        if (typeof data.price === 'number') {
          processedPrice = data.price.toString();
        } 
        // Handle string prices with potential currency symbols
        else if (typeof data.price === 'string') {
          // Remove currency symbols and non-numeric characters except decimal point
          processedPrice = data.price.replace(/[^\d.]/g, '');
          
          // Check if it's a valid number after cleaning
          if (processedPrice && !isNaN(parseFloat(processedPrice))) {
            processedPrice = parseFloat(processedPrice).toString();
          }
        }
      }
      
      console.log('Processed price for AI suggestion:', processedPrice);
      
      return {
        description: data.description || '',
        price: processedPrice,
        currency: data.currency || 'ILS',
        estimatedCompletionDate: data.estimatedCompletionDate || data.fulfillmentDate || data.dueDate || '',
        providerContactInfo: data.providerContactInfo || data.contactInfo || defaultContactInfo
      };
    }
    
    // Fallback to empty object if data is invalid
    return { 
      description: '', 
      price: '', 
      currency: 'ILS', 
      estimatedCompletionDate: '', 
      providerContactInfo: defaultContactInfo 
    };
  };

  // Fetch AI suggestion for the offer
  const fetchAiSuggestion = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await getAiSuggestedOffer(willId);
      const formattedSuggestion = processAiSuggestion(response);
      setAiSuggestion(formattedSuggestion);
    } catch (err) {
      console.error('Error fetching AI suggestion:', err);
      setError('Failed to get AI suggestion. You can still create an offer manually.');
    } finally {
      setLoading(false);
    }
  };

  // Regenerate AI suggestion
  const handleRegenerateSuggestion = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await regenerateAiSuggestion(willId);
      const formattedSuggestion = processAiSuggestion(response);
      setAiSuggestion(formattedSuggestion);
      toast.success('New AI suggestion generated');
    } catch (err) {
      console.error('Error regenerating AI suggestion:', err);
      setError('Failed to regenerate AI suggestion.');
      toast.error('Failed to regenerate suggestion');
    } finally {
      setLoading(false);
    }
  };

  // Handle successful offer submission
  const handleSuccess = (offer: any) => {
    if (onSuccess) {
      onSuccess(offer);
    }
    onClose();
  };

  return (
    <div 
      className={`fixed right-0 top-0 z-40 h-screen w-full sm:w-[450px] md:w-[500px] bg-white shadow-lg transform transition-transform duration-300 ease-in-out border-l border-gray-200 
      ${isOpen ? 'translate-x-0' : 'translate-x-full'} overflow-y-auto`}
    >
      <div className="sticky top-0 z-10 flex items-center justify-between px-4 py-3 border-b bg-white">
        <h3 className="text-lg font-medium truncate">Make an Offer: {willTitle}</h3>
        <button 
          onClick={onClose}
          className="p-1 rounded-sm opacity-70 hover:opacity-100 focus:outline-none ml-2 flex-shrink-0"
        >
          <X className="h-5 w-5" />
          <span className="sr-only">Close</span>
        </button>
      </div>
      
      <div className="p-4">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-8">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mb-4"></div>
            <p className="text-gray-600">Creating AI-suggested offer...</p>
          </div>
        ) : (
          <>
            {aiSuggestion && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-5">
                <div className="flex justify-between items-start">
                  <div>
                    <h4 className="text-sm font-medium flex items-center">
                      <svg className="w-4 h-4 mr-1 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z"></path>
                      </svg>
                      AI-Suggested Offer
                    </h4>
                    <p className="text-xs text-gray-600 mt-1">
                      We've created a suggested offer based on the want details.
                    </p>
                  </div>
                  <button
                    onClick={handleRegenerateSuggestion}
                    className="text-blue-600 hover:text-blue-800 text-xs font-medium flex items-center"
                    disabled={loading}
                  >
                    <RefreshCw className="h-3 w-3 mr-1" />
                    Regenerate
                  </button>
                </div>
              </div>
            )}
            
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4 text-sm text-red-600">
                {error}
              </div>
            )}
            
            <OfferForm 
              willId={willId}
              willMedia={willMedia}
              onCancel={onClose}
              onSuccess={handleSuccess}
              defaultContactInfo={defaultContactInfo}
              initialData={aiSuggestion}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default OfferFormModal; 